import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { getAllPlans, addPlan, updatePlan, changePlanStatus, deletePlan } from '../../api/pazaryeri';
import { useLoader } from '../../context/LoaderContext';

const Marketplace = () => {
    const { showLoader, hideLoader } = useLoader();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        description: '',
        price: '',
        durationDays: 30, // Default 30 days
        isActive: true
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Delete Confirmation
    const [deleteId, setDeleteId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const fetchPlans = async () => {
        showLoader();
        try {
            const res = await getAllPlans();
            if (Array.isArray(res)) {
                setPlans(res);
            } else if (res.data && Array.isArray(res.data)) {
                setPlans(res.data);
            } else {
                setPlans([]);
            }
        } catch (error) {
            console.error("Plans fetch error:", error);
            toast.error("Plan listesi yüklenirken hata oluştu.");
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    // Form Handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const openAddModal = () => {
        setIsEdit(false);
        setFormData({
            id: null,
            name: '',
            description: '',
            price: '',
            durationDays: 30,
            isActive: true
        });
        setShowModal(true);
    };

    const openEditModal = (plan) => {
        setIsEdit(true);
        setFormData({
            id: plan.id,
            name: plan.name,
            description: plan.description,
            price: plan.price,
            durationDays: plan.durationDays,
            isActive: plan.isActive
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoader();
        setIsSubmitting(true);

        try {
            // Prepare payload
            const payload = {
                name: formData.name,
                description: formData.description,
                price: Number(formData.price),
                durationDays: Number(formData.durationDays),
                isActive: formData.isActive
            };

            if (isEdit) {
                await updatePlan(formData.id, payload);
                toast.success("Plan başarıyla güncellendi.");
            } else {
                await addPlan(payload);
                toast.success("Plan başarıyla eklendi.");
            }
            closeModal();
            fetchPlans();
        } catch (error) {
            console.error("Save error:", error);
            if (error.errors) {
                const firstError = Object.values(error.errors).flat()[0];
                toast.error(firstError || "Validasyon hatası.");
            } else {
                toast.error(error.message || "Kaydetme başarısız.");
            }
        } finally {
            setIsSubmitting(false);
            hideLoader();
        }
    };

    // Status Change
    const handleStatusChange = async (id) => {
        showLoader();
        try {
            const res = await changePlanStatus(id);
            toast.success(res.message || "Durum güncellendi.");
            fetchPlans();
        } catch (error) {
            console.error("Status change error:", error);
            toast.error(error.message || "Durum değiştirilemedi.");
        } finally {
            hideLoader();
        }
    };

    // Delete Handlers
    const confirmDelete = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const performDelete = async () => {
        if (!deleteId) return;

        showLoader();
        try {
            await deletePlan(deleteId);
            toast.success("Plan başarıyla silindi.");
            setShowDeleteModal(false);
            setDeleteId(null);
            fetchPlans();
        } catch (error) {
            console.error("Delete error:", error);
            toast.error(error.message || "Silme işlemi başarısız.");
        } finally {
            hideLoader();
        }
    };


    return (
        <>
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                    <h6 className="text-lg fw-semibold mb-0">Premium Paketler (Pazaryeri)</h6>
                    <button onClick={openAddModal} className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2">
                        <Icon icon="heroicons:plus-circle" className="text-xl" />
                        Yeni Paket Ekle
                    </button>
                </div>
                <div className="card-body p-24">
                    <div className="table-responsive">
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">Paket Adı</th>
                                    <th scope="col">Açıklama</th>
                                    <th scope="col">Fiyat</th>
                                    <th scope="col">Süre (Gün)</th>
                                    <th scope="col">Durum</th>
                                    <th scope="col" className="text-center">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plans.length > 0 ? (
                                    plans.map((item) => (
                                        <tr key={item.id}>
                                            <td><span className="text-primary-600 fw-bold">{item.name}</span></td>
                                            <td>
                                                <span className="text-secondary-light text-sm text-truncate d-inline-block" style={{ maxWidth: '250px' }} title={item.description}>
                                                    {item.description}
                                                </span>
                                            </td>
                                            <td><span className="text-success-600 fw-bold">{item.price} ₺</span></td>
                                            <td><span className="text-secondary-light">{item.durationDays} Gün</span></td>
                                            <td>
                                                <div className="form-check form-switch d-flex align-items-center justify-content-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={item.isActive}
                                                        onChange={() => handleStatusChange(item.id)}
                                                    />
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                    <button
                                                        onClick={() => openEditModal(item)}
                                                        className="w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center hover-bg-primary-600 hover-text-white transition-2"
                                                        title="Düzenle"
                                                    >
                                                        <Icon icon="heroicons:pencil-square" className="text-xl" />
                                                    </button>
                                                    <button
                                                        onClick={() => confirmDelete(item.id)}
                                                        className="w-32-px h-32-px bg-danger-50 text-danger-600 rounded-circle d-inline-flex align-items-center justify-content-center hover-bg-danger-600 hover-text-white transition-2"
                                                        title="Sil"
                                                    >
                                                        <Icon icon="heroicons:trash" className="text-xl" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-24">
                                            <p className="text-secondary-light mb-0">Kayıtlı paket bulunamadı.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header">
                                <h5 className="modal-title">{isEdit ? 'Paket Düzenle' : 'Yeni Paket Ekle'}</h5>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Paket Adı</label>
                                            <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Örn: Premium" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Süre (Gün)</label>
                                            <input type="number" className="form-control" name="durationDays" value={formData.durationDays} onChange={handleInputChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Fiyat (₺)</label>
                                            <input type="number" step="0.01" className="form-control" name="price" value={formData.price} onChange={handleInputChange} required />
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label fw-semibold">Açıklama</label>
                                            <textarea className="form-control" name="description" rows="3" value={formData.description} onChange={handleInputChange} required placeholder="Paket detayları..."></textarea>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" name="isActive" id="isActiveCheck" checked={formData.isActive} onChange={handleInputChange} />
                                                <label className="form-check-label fw-medium" htmlFor="isActiveCheck">
                                                    Aktif
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModal}>İptal</button>
                                    <button type="submit" className="btn btn-primary-600" disabled={isSubmitting}>
                                        {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header bg-danger-focus text-danger-main">
                                <h5 className="modal-title d-flex align-items-center gap-2">
                                    <Icon icon="heroicons:exclamation-triangle" />
                                    Paket Silme Onayı
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0 fw-medium">Bu paketi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Vazgeç</button>
                                <button type="button" className="btn btn-danger-600" onClick={performDelete}>Evet, Sil</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Marketplace;
